import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { DOCUMENT_CATEGORY_KEYS, getRequiredDocumentCategoryKeys } from '../../../utils/constants/packagingRoles';

const CATEGORY_LABELS = {
    SUPPLIER_DOCS: 'Doklady dodávateľov',
    CONFORMITY_ASSESSMENT: 'Posúdenie zhody',
    EU_DECLARATION: 'EÚ vyhlásenie',
    TECHNICAL_DOCS: 'Technická dokumentácia',
    DISTRIBUTOR_VERIFICATION: 'Overenie (distribútor)',
    PRODUCER_VERIFICATION: 'Registrácia (výrobca)',
};

// DOCUMENT_CATEGORY_KEYS pokrýva len kategórie zhotoviteľa - DISTRIBUTOR_VERIFICATION a
// PRODUCER_VERIFICATION sa dopĺňajú samostatne, keďže sa vzťahujú len na jednotky s rolou
// DISTRIBUTOR, resp. PRODUCER (viď getRequiredDocumentCategoryKeys).
const ALL_CATEGORY_KEYS = [...DOCUMENT_CATEGORY_KEYS, 'DISTRIBUTOR_VERIFICATION', 'PRODUCER_VERIFICATION'];

const COLOR_SPLNENE = '#198754';
const COLOR_NESPLNENE = '#F0AD4E';

const isTabCategoryComplete = (unit, key) => {
    if (key === 'CONFORMITY_ASSESSMENT') return !!unit.conformitySummary?.complete;
    if (key === 'TECHNICAL_DOCS') return !!unit.technicalDocumentationStatus?.current;
    if (key === 'EU_DECLARATION') return !!unit.euDeclarationStatus?.current;
    if (key === 'SUPPLIER_DOCS') return !!unit.supplierDocsConfirmedAt;
    if (key === 'DISTRIBUTOR_VERIFICATION') return !!unit.distributorVerificationSummary?.complete;
    if (key === 'PRODUCER_VERIFICATION') return !!unit.producerVerificationSummary?.complete;
    return false;
};

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const { splnene, nesplnene, total } = payload[0].payload;
    return (
        <div style={{
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            padding: '10px 16px',
            boxShadow: '0 4px 16px rgba(11,17,87,0.10)',
            color: '#0B1157',
            fontSize: 13,
        }}>
            <p style={{ margin: 0, fontWeight: 700, marginBottom: 6 }}>{label}</p>
            <p style={{ margin: 0, color: COLOR_SPLNENE }}>Splnené: <strong>{splnene}</strong></p>
            <p style={{ margin: 0, color: '#C97D2C' }}>Nesplnené: <strong>{nesplnene}</strong></p>
            <p style={{ margin: '4px 0 0', color: '#718094' }}>Spolu: {total}</p>
        </div>
    );
};

const DocumentationStatusChart = ({ units }) => {
    const data = ALL_CATEGORY_KEYS.map((key) => {
        const applicable = units.filter((u) => getRequiredDocumentCategoryKeys(u.baseRole, u.producerRole).includes(key));
        const splnene = applicable.filter((u) => isTabCategoryComplete(u, key)).length;
        return {
            label: CATEGORY_LABELS[key],
            splnene,
            nesplnene: applicable.length - splnene,
            total: applicable.length,
        };
    }).filter((row) => row.total > 0);

    const isEmpty = data.length === 0;

    return (
        <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: '24px 24px 16px',
            boxShadow: '0 2px 12px rgba(11,17,87,0.07)',
            height: '100%',
        }}>
            <div className="mb-4">
                <h6 style={{ color: '#0B1157', fontWeight: 700, margin: 0, fontSize: 16 }}>
                    Stav dokumentácie
                </h6>
                <p style={{ color: '#718094', fontSize: 13, margin: '2px 0 0' }}>
                    Počet obalových jednotiek podľa splnenia jednotlivých kategórií
                </p>
            </div>

            {isEmpty ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: 260, color: '#718094', fontSize: 14 }}>
                    Zatiaľ nie sú žiadne dáta.
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 16, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis
                            type="number"
                            allowDecimals={false}
                            tick={{ fill: '#718094', fontSize: 12 }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <YAxis
                            type="category"
                            dataKey="label"
                            tick={{ fill: '#0B1157', fontSize: 12, fontWeight: 600 }}
                            axisLine={false}
                            tickLine={false}
                            width={140}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f4f5fe' }} />
                        <Legend
                            formatter={(value) => (value === 'splnene' ? 'Splnené' : 'Nesplnené')}
                            wrapperStyle={{ fontSize: 12, color: '#718094' }}
                        />
                        <Bar dataKey="splnene" stackId="a" fill={COLOR_SPLNENE} radius={[0, 0, 0, 0]} maxBarSize={22} />
                        <Bar dataKey="nesplnene" stackId="a" fill={COLOR_NESPLNENE} radius={[0, 4, 4, 0]} maxBarSize={22} />
                    </BarChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default DocumentationStatusChart;
